import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Loader2, Plus, Trash2, ListOrdered } from "lucide-react";

const REGIMI_OPTIONS = [
  { value: "esente_art10", label: "Esente Art.10" },
  { value: "iva_4", label: "IVA 4%" },
  { value: "iva_22", label: "IVA 22%" },
  { value: "esclusa_art15", label: "Esclusa Art.15" },
];

const REGIME_VALUES = new Set(REGIMI_OPTIONS.map((o) => o.value));

function normalizeRegime(v) {
  return v && REGIME_VALUES.has(v) ? v : "iva_22";
}

function newRowKey() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `k-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function rowFromVoce(v) {
  return {
    _key: newRowKey(),
    descrizione: String(v?.descrizione ?? ""),
    quantita: Number(v?.quantita) > 0 ? Number(v.quantita) : 1,
    prezzo_unitario: Number(v?.prezzo_unitario) >= 0 && !Number.isNaN(Number(v?.prezzo_unitario)) ? Number(v.prezzo_unitario) : 0,
    regime_iva: normalizeRegime(v?.regime_iva),
    tipo: v?.tipo || undefined,
    prima_nota_spesa_id: v?.prima_nota_spesa_id || undefined,
    prima_nota_consumo_id: v?.prima_nota_consumo_id || undefined,
  };
}

function validateRows(rows) {
  if (!rows.length) return "Aggiungi almeno una riga.";
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    if (!String(r.descrizione || "").trim()) {
      return `Riga ${i + 1}: inserire la descrizione.`;
    }
    const q = Number(r.quantita);
    if (!(q > 0) || Number.isNaN(q)) {
      return `Riga ${i + 1}: quantità non valida.`;
    }
    const p = Number(r.prezzo_unitario);
    if (p < 0 || Number.isNaN(p)) {
      return `Riga ${i + 1}: prezzo non valido.`;
    }
  }
  return null;
}

function rowsToPayload(rows) {
  return rows.map((r) => {
    const o = {
      descrizione: String(r.descrizione).trim(),
      quantita: Number(r.quantita),
      prezzo_unitario: Number(r.prezzo_unitario),
      regime_iva: r.regime_iva,
    };
    if (r.tipo) o.tipo = r.tipo;
    if (r.prima_nota_spesa_id) o.prima_nota_spesa_id = r.prima_nota_spesa_id;
    if (r.prima_nota_consumo_id) o.prima_nota_consumo_id = r.prima_nota_consumo_id;
    return o;
  });
}

/**
 * Dialog per modificare le voci di una fattura con campi guidati (nessun JSON).
 */
export default function ModificaVociFatturaDialog({
  open,
  onOpenChange,
  initialVoci,
  saving,
  onSave,
}) {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (!open) return;
    const list = Array.isArray(initialVoci) ? initialVoci : [];
    setRows(list.map((v) => rowFromVoce(v)));
  }, [open, initialVoci]);

  const updateRow = useCallback((key, patch) => {
    setRows((prev) => prev.map((r) => (r._key === key ? { ...r, ...patch } : r)));
  }, []);

  const addRow = useCallback(() => {
    setRows((prev) => [
      ...prev,
      {
        _key: newRowKey(),
        descrizione: "",
        quantita: 1,
        prezzo_unitario: 0,
        regime_iva: "esente_art10",
      },
    ]);
  }, []);

  const removeRow = useCallback((key) => {
    setRows((prev) => {
      if (prev.length <= 1) {
        toast.error("Serve almeno una voce in fattura.");
        return prev;
      }
      return prev.filter((r) => r._key !== key);
    });
  }, []);

  const handleSalva = async () => {
    const err = validateRows(rows);
    if (err) {
      toast.error(err);
      return;
    }
    try {
      await onSave(rowsToPayload(rows));
    } catch {
      /* errore gestito dal genitore */
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] w-[calc(100vw-1.5rem)] flex flex-col gap-0 overflow-hidden p-0 sm:w-full">
        <DialogHeader className="shrink-0 border-b border-slate-100 px-5 py-4 pr-12 text-left">
          <DialogTitle className="flex items-center gap-2 text-left">
            <ListOrdered className="w-5 h-5 text-emerald-600 shrink-0" />
            Modifica voci fattura
          </DialogTitle>
          <DialogDescription className="text-left text-sm">
            Descrizione, quantità, prezzo unitario e aliquota/regime IVA. Salva per ricalcolare totali e rigenerare l&apos;XML.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-3 space-y-3">
          <div className="hidden sm:grid sm:grid-cols-[1fr_4.5rem_6.5rem_9.5rem_2.5rem] gap-2 text-xs font-medium text-slate-500 px-1">
            <span>Descrizione</span>
            <span className="text-center">Qtà</span>
            <span className="text-right pr-1">Prezzo €</span>
            <span>IVA / regime</span>
            <span />
          </div>

          <div className="space-y-3">
            {rows.map((r, idx) => {
              const lineTot = (Number(r.quantita) || 0) * (Number(r.prezzo_unitario) || 0);
              const lineTotLabel = Number.isFinite(lineTot)
                ? `€ ${lineTot.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                : "—";
              return (
              <div
                key={r._key}
                className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 sm:border-0 sm:bg-transparent sm:p-0 sm:grid sm:grid-cols-[1fr_4.5rem_6.5rem_9.5rem_2.5rem] sm:gap-2 sm:items-start"
              >
                <div className="space-y-1 sm:space-y-0">
                  <Label className="text-xs text-slate-500 sm:sr-only">Descrizione riga {idx + 1}</Label>
                  <div className="flex flex-wrap items-center gap-2">
                    <Input
                      value={r.descrizione}
                      onChange={(e) => updateRow(r._key, { descrizione: e.target.value })}
                      placeholder="Descrizione servizio o voce"
                      className="text-sm flex-1 min-w-[160px]"
                    />
                    {(r.prima_nota_spesa_id || r.prima_nota_consumo_id) && (
                      <Badge variant="secondary" className="text-[10px] shrink-0">
                        Prima nota
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:contents gap-2 mt-2 sm:mt-0">
                  <div className="space-y-1 sm:space-y-0">
                    <Label className="text-xs text-slate-500 sm:sr-only">Quantità</Label>
                    <Input
                      type="number"
                      min={0.01}
                      step="0.01"
                      value={r.quantita}
                      onChange={(e) => updateRow(r._key, { quantita: e.target.value })}
                      className="text-sm sm:text-center"
                    />
                  </div>
                  <div className="space-y-1 sm:space-y-0">
                    <Label className="text-xs text-slate-500 sm:sr-only">Prezzo unit. €</Label>
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      value={r.prezzo_unitario}
                      onChange={(e) => updateRow(r._key, { prezzo_unitario: e.target.value })}
                      className="text-sm"
                    />
                  </div>
                  <div className="col-span-2 sm:col-span-1 space-y-1 sm:space-y-0">
                    <Label className="text-xs text-slate-500 sm:sr-only">Regime IVA</Label>
                    <Select
                      value={r.regime_iva}
                      onValueChange={(v) => updateRow(r._key, { regime_iva: v })}
                    >
                      <SelectTrigger className="text-sm h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {REGIMI_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex justify-between sm:flex-col sm:items-center gap-1 pt-1 sm:pt-0">
                    <span className="text-xs text-slate-500 sm:order-2">
                      Tot. <span className="font-medium text-slate-700 tabular-nums">{lineTotLabel}</span>
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 text-red-600 hover:text-red-700 hover:bg-red-50 sm:order-1"
                      onClick={() => removeRow(r._key)}
                      title="Rimuovi riga"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
            })}
          </div>

          <Button type="button" variant="outline" size="sm" className="w-full sm:w-auto border-dashed" onClick={addRow}>
            <Plus className="w-4 h-4 mr-1" />
            Aggiungi riga
          </Button>
        </div>

        <DialogFooter className="shrink-0 border-t border-slate-100 px-5 py-4 gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Annulla
          </Button>
          <Button type="button" onClick={handleSalva} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Salva modifiche
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
